// Single source of truth for the homepage FAQ.
//
// Consumed by BOTH:
//   • the build/prerender layer (scripts/seo-config.js, plain Node ESM) to emit
//     the FAQPage JSON-LD, and
//   • the React app (src/i18n/locales/*.ts, via allowJs) to render the on-page
//     FAQ accordion.
//
// Keeping one copy prevents the on-page FAQ and the structured-data FAQ from
// drifting apart (which would make the JSON-LD misrepresent the page — a GEO/SEO
// correctness issue). Edit the questions/answers here only.

/** @typedef {{ question: string; answer: string }} FaqItem */

/** @type {FaqItem[]} */
export const FAQ_SL = [
  {
    question: 'Kako natančna so vaša poročila?',
    answer:
      'Poročilo združuje podatke GURS in zemljiške knjige z evidentiranimi kupoprodajami ETN ter primerljivimi oglasi. Informativna vrednost upošteva razpoložljive cenovne podatke; kadar je primerljivih prodaj malo, lahko večjo vlogo dobijo oglasi ali modelirana posplošena vrednost GURS kot zasilna referenca. Poročilo pokaže uporabljene vire, število primerljivih poslov in razpon ocene. Umetno inteligenco uporabljamo za razlago in besedilo poročila, ne za sam izračun vrednosti.',
  },
  {
    question: 'Kako hitro prejmem poročilo?',
    answer: 'Poročilo prejmete v vaš e-poštni predal v roku 24 ur po uspešnem plačilu.',
  },
  {
    question: 'Kako dolgo veljajo krediti?',
    answer:
      'Krediti veljajo 1 leto od datuma nakupa. Po preteku tega obdobja krediti potečejo. V svoji nadzorni plošči lahko vedno preverite, kdaj bodo vaši krediti potekli.',
  },
  {
    question: 'Kakšen format ima poročilo?',
    answer:
      'Poročilo prejmete kot profesionalno oblikovan PDF dokument, ki je pripravljen za tisk ali elektronsko deljenje. Povprečno poročilo obsega 20-30 strani.',
  },
  {
    question: 'Ali lahko poročilo uporabim za uradne namene?',
    answer:
      'Naše poročilo je informativne narave in temelji na uradnih podatkih. Za uradne namene (sodišče, banka) priporočamo pooblaščeno ocenjevanje nepremičnin.',
  },
  {
    question: 'Katere vrste nepremičnin pokrivate?',
    answer: 'Zaenkrat pokrivamo stanovanja in hiše, ki so evidentirane v GURS.',
  },
  {
    question: 'Kaj če nisem zadovoljen s poročilom?',
    answer:
      'Če poročilo ne izpolnjuje vaših pričakovanj, nam pišite na podporo in poskusili bomo rešiti vašo težavo.',
  },
  {
    question: 'Ali potrebujem uporabniški račun?',
    answer:
      'Da, za nakup kreditov in dostop do poročil potrebujete uporabniški račun. Registracija je brezplačna in traja le nekaj sekund.',
  },
];

/** @type {FaqItem[]} */
export const FAQ_EN = [
  {
    question: 'How accurate are your reports?',
    answer:
      'The report combines GURS and land registry data with recorded ETN sale prices and comparable listings. The indicative value uses the available price evidence; when comparable sales are scarce, listings or the modelled GURS value may carry more weight as a fallback reference. The report shows the sources used, the number of comparable sales, and the estimate range. We use AI to explain the findings and write the report, not to produce the valuation itself.',
  },
  {
    question: 'How quickly will I receive my report?',
    answer: "You'll receive your report by email within 24 hours of successful payment.",
  },
  {
    question: 'How long are credits valid?',
    answer:
      'Credits are valid for 1 year from the date of purchase. After that period, they expire. You can always check when your credits expire in your dashboard.',
  },
  {
    question: 'What format is the report in?',
    answer:
      "You'll receive a professionally designed PDF document, ready for printing or sharing. The average report is 20–30 pages.",
  },
  {
    question: 'Can I use the report for official purposes?',
    answer:
      'Our report is for informational purposes and is based on official data. For official use (court, bank), we recommend a licensed property appraisal.',
  },
  {
    question: 'What types of properties do you cover?',
    answer: 'We currently cover apartments and houses registered in GURS.',
  },
  {
    question: "What if I'm not satisfied with the report?",
    answer:
      "If the report doesn't meet your expectations, contact support and we'll do our best to resolve the issue.",
  },
  {
    question: 'Do I need an account?',
    answer:
      'Yes, you need an account to purchase credits and access reports. Registration is free and takes just a few seconds.',
  },
];

/** @type {FaqItem[]} */
export const FAQ_DE = [
  {
    question: 'Wie genau sind Ihre Berichte?',
    answer:
      'Der Bericht verbindet GURS- und Grundbuchdaten mit erfassten ETN-Kaufpreisen und vergleichbaren Angeboten. Der informative Wert nutzt die verfügbaren Preisdaten; wenn wenige vergleichbare Verkäufe vorliegen, können Angebote oder der modellierte GURS-Wert als Ersatzreferenz stärker ins Gewicht fallen. Der Bericht zeigt die verwendeten Quellen, die Zahl vergleichbarer Verkäufe und die Schätzspanne. Künstliche Intelligenz nutzen wir für die Erläuterung und den Text des Berichts, nicht für die Berechnung des Werts.',
  },
  {
    question: 'Wie schnell erhalte ich meinen Bericht?',
    answer: 'Sie erhalten Ihren Bericht per E-Mail innerhalb von 24 Stunden nach erfolgreicher Zahlung.',
  },
  {
    question: 'Wie lange sind Credits gültig?',
    answer:
      'Credits sind 1 Jahr ab Kaufdatum gültig. Danach verfallen sie. Im Dashboard können Sie jederzeit prüfen, wann Ihre Credits ablaufen.',
  },
  {
    question: 'In welchem Format liegt der Bericht vor?',
    answer:
      'Sie erhalten ein professionell gestaltetes PDF-Dokument, bereit zum Drucken oder Teilen. Der durchschnittliche Bericht umfasst 20–30 Seiten.',
  },
  {
    question: 'Kann ich den Bericht für offizielle Zwecke nutzen?',
    answer:
      'Unser Bericht dient Informationszwecken und basiert auf offiziellen Daten. Für offizielle Zwecke (Gericht, Bank) empfehlen wir eine lizenzierte Immobilienbewertung.',
  },
  {
    question: 'Welche Immobilientypen decken Sie ab?',
    answer: 'Derzeit decken wir Wohnungen und Häuser ab, die im GURS registriert sind.',
  },
  {
    question: 'Was, wenn ich mit dem Bericht nicht zufrieden bin?',
    answer:
      'Wenn der Bericht Ihre Erwartungen nicht erfüllt, kontaktieren Sie den Support — wir tun unser Bestes, das Problem zu lösen.',
  },
  {
    question: 'Brauche ich ein Konto?',
    answer:
      'Ja, Sie benötigen ein Konto, um Credits zu kaufen und auf Berichte zuzugreifen. Die Registrierung ist kostenlos und dauert nur wenige Sekunden.',
  },
];

/** @type {FaqItem[]} */
export const FAQ_RU = [
  {
    question: 'Насколько точны Ваши отчёты?',
    answer:
      'Отчёт объединяет данные GURS и земельного реестра с зарегистрированными ценами сделок ETN и сопоставимыми объявлениями. Ориентировочная стоимость использует доступные ценовые данные; если сопоставимых продаж мало, больший вес могут получить объявления или смоделированная стоимость GURS как резервный ориентир. В отчёте указаны использованные источники, число сопоставимых продаж и диапазон оценки. Искусственный интеллект мы используем для пояснений и текста отчёта, а не для расчёта стоимости.',
  },
  {
    question: 'Как быстро я получу отчёт?',
    answer: 'Вы получите отчёт по электронной почте в течение 24 часов после успешной оплаты.',
  },
  {
    question: 'Как долго действуют кредиты?',
    answer:
      'Кредиты действительны 1 год с даты покупки. По истечении этого срока они аннулируются. Вы всегда можете проверить срок действия кредитов в личном кабинете.',
  },
  {
    question: 'В каком формате предоставляется отчёт?',
    answer:
      'Вы получите профессионально оформленный PDF-документ, готовый к печати или отправке. Средний объём отчёта — 20–30 страниц.',
  },
  {
    question: 'Могу ли я использовать отчёт для официальных целей?',
    answer:
      'Наш отчёт носит информационный характер и основан на официальных данных. Для официального использования (суд, банк) мы рекомендуем лицензированную оценку недвижимости.',
  },
  {
    question: 'Какие типы недвижимости Вы охватываете?',
    answer: 'В настоящее время мы охватываем квартиры и дома, зарегистрированные в GURS.',
  },
  {
    question: 'Что если меня не устраивает отчёт?',
    answer:
      'Если отчёт не соответствует Вашим ожиданиям, свяжитесь со службой поддержки, и мы сделаем всё возможное для решения проблемы.',
  },
  {
    question: 'Нужна ли учётная запись?',
    answer:
      'Да, для покупки кредитов и доступа к отчётам необходима учётная запись. Регистрация бесплатна и занимает всего несколько секунд.',
  },
];

/** @type {Record<'sl'|'en'|'de'|'ru', FaqItem[]>} */
export const FAQ_BY_LOCALE = { sl: FAQ_SL, en: FAQ_EN, de: FAQ_DE, ru: FAQ_RU };
